import React from "react"
import { FlexLayout } from "../layout/flex/FlexLayout"
import { TextInput, ValidationResult } from "./TextInput"
import { renderTestID } from "../helpers/renderTestID"
import { getDateComponentOrder, getMaxDayForYearMonth, parseDateString, parseMonth } from "./DateInputUtils"
import assert from "assert"
import { withLocale } from "../i18n/withLocale"
import { Locale } from "../i18n/LocaleContext"

interface InjectedProps {
  locale: Locale
}

export interface DateInputProps extends InjectedProps {
  /**
   * Form field name.
   *
   * The form is submitted as {
   *   [$name]: "yyyy-mm-dd" or "yyyy-mm" if day is missing
   *   [$name-year]: "yyyy",            // as user input
   *   [$name-month]: "m" or "jan",     // as user input
   *   [$name-day]: "d",                // as user input
   * }
   */
  name: string

  /**
   * Initial value for the field.
   *
   * Must be in the format of yyyy-mm-dd. Otherwise the value is silently ignored.
   */
  initialValue?: string

  /**
   * Triggered when user finishes typing a valid date and leaves the input box.
   *
   * `newValue` is of format `yyyy-mm-dd` or `yyyy-mm` (if day is empty)
   */
  onChange?: (newValue: string) => void

  /**
   * Is day optional? When set to true, please provide `defaultDay`.
   *
   * @default false
   */
  dayOptional?: boolean

  /**
   * Default day to use when `dayOptional` is true and the user leaves the day field empty.
   *
   * - `"first_day_of_month"`: defaults to the 1st of the selected month
   *    This should be used for expiration dates.
   * - `"last_day_of_month"`: defaults to the last day of the selected month/year
   * - `undefined`: no default day, date is reported as `yyyy-mm` (current behavior)
   *
   * Only takes effect when `dayOptional` is true.
   */
  defaultDay?: "first_day_of_month" | "last_day_of_month"

  /**
   * Inclusive
   */
  maxYear?: number
  /**
   * Inclusive
   */
  minYear?: number

  /**
   * The prefix of testID to be added to the year, month and day components.
   * Each will have ${testIDPrefix}-year,${testIDPrefix}-month, ${testIDPrefix}-day
   * as their testID, respectively.
   */
  testIDPrefix?: string

  /**
   * Automatically focus the first input
   *
   * @default false
   */
  autoFocus?: boolean
}

interface DateInputState {
  year: string
  isYearValid: boolean
  month: string
  isMonthValid: boolean
  day: string
  isDayValid: boolean
}

interface DateInputStringIDs {
  wrongYear: string
  maxYear: string
  minYear: string
  wrongMonth: string
  wrongDay: string
}

/**
 * `DateInput` provides a common pattern for inputting dates. It has 3
 * separate internal inputs, for year, month and day, respectively.
 * - The inputs are ordered differently, depending on the locale.
 * - It has builtin validations for the format and the date range.
 *
 * UI proposal: https://quip-amazon.com/bO4TAx24CbMZ#JMM9AA4I1gy
 * Design: https://quip-amazon.com/GLBMApFD913i/Alchemy-TaskUI-Expiration-Dates
 */
class DateInputBase extends React.Component<DateInputProps, DateInputState> {
  public static defaultStringIDs?: DateInputStringIDs

  public constructor(props: DateInputProps) {
    super(props)

    const date = parseDateString(this.props.initialValue || "")
    if (date.year) {
      this.state = {
        ...date,
        isYearValid: true,
        isMonthValid: true,
        isDayValid: true,
      }
    } else {
      this.state = {
        ...date,
        isYearValid: false,
        isMonthValid: false,
        isDayValid: !!this.props.dayOptional,
      }
    }

    this.validateYear = this.validateYear.bind(this)
    this.validateMonth = this.validateMonth.bind(this)
    this.validateDay = this.validateDay.bind(this)

    this.handleYearChange = this.handleYearChange.bind(this)
    this.handleMonthChange = this.handleMonthChange.bind(this)
    this.handleDayChange = this.handleDayChange.bind(this)
    this.postDateChange = this.postDateChange.bind(this)

    this.handleYearInput = this.handleYearInput.bind(this)
    this.handleMonthInput = this.handleMonthInput.bind(this)
    this.handleDayInput = this.handleDayInput.bind(this)
  }

  private validateYear(value: string): ValidationResult {
    const fn = (): ValidationResult => {
      if (!/^\d{4}$/.test(value)) {
        return {
          valid: false,
          failureMessage: {
            stringID: DateInputBase.defaultStringIDs?.wrongYear,
            defaultString: "Enter a valid year in 4 digits (YYYY)"
          }
        }
      }
      const year = Number(value)
      if (typeof this.props.maxYear !== "undefined") {
        if (year > this.props.maxYear) {
          return {
            valid: false,
            failureMessage: {
              stringID: DateInputBase.defaultStringIDs?.maxYear,
              defaultString: `Year max value: {maxYear}`,
              parameters: {
                maxYear: this.props.maxYear
              }
            }
          }
        }
      }
      if (typeof this.props.minYear !== "undefined") {
        if (year < this.props.minYear) {
          return {
            valid: false,
            failureMessage: {
              stringID: DateInputBase.defaultStringIDs?.minYear,
              defaultString: `Year min value: {minYear}`,
              parameters: {
                minYear: this.props.minYear
              }
            }
          }
        }
      }
      return {
        valid: true
      }
    }
    const result = fn()
    if (!result.valid) {
      this.setState({isYearValid: false})
    }
    return result
  }

  private validateMonth(value: string): ValidationResult {
    const fn = () => {
      const failureMessage = {
        stringID: DateInputBase.defaultStringIDs?.wrongMonth,
        defaultString: "Enter a valid month (1-12 or Jan). No case sensitivity"
      }
      if (!parseMonth(value, this.props.locale.locale)) {
        return {
          valid: false,
          failureMessage,
        }
      }
      return {
        valid: true,
      }
    }
    const result = fn()
    if (!result.valid) {
      this.setState({isMonthValid: false})
    }
    return result
  }

  private handleYearChange(value: string) {
    this.setState({year: value, isYearValid: true}, this.postDateChange)
  }

  private handleMonthChange(value: string) {
    this.setState({month: value, isMonthValid: true}, this.postDateChange)
  }

  private handleDayChange(value: string) {
    this.setState({day: value, isDayValid: true}, this.postDateChange)
  }

  private postDateChange() {
    if (!this.props.onChange) {
      return
    }
    const state = this.state
    if (state.isYearValid
      && state.isMonthValid
      && state.isDayValid
      // New change to year and month can change the validity of `day`
      // so we need to recheck
      && this.isDayValid(this.state.day).valid) {
        const curDate = this.getCurrentDate()
        this.props.onChange(curDate)
      }
  }

  private validateDay(value: string): ValidationResult {
    const result = this.isDayValid(value)
    if (!result.valid) {
      this.setState({isDayValid: false})
    }
    return result
  }

  private isDayValid(value: string): ValidationResult {
    if (!value && this.props.dayOptional) {
      return {
        valid: true,
      }
    }
    const failureMessage = {
      stringID: DateInputBase.defaultStringIDs?.wrongDay,
      // The Alchemy version will want the error string with the variable `daysInMonth`
      // still in it. This could pose an issue to our Translator, which should take care
      // of all variables in the string.
      // Double check if this is an issue when we do the migration to Alchemy's DateInput.
      defaultString: "Enter a valid day (1-{daysInMonth})",
      parameters: {
        daysInMonth: 31,
      }
    }
    if (!/^\d{1,2}$/.test(value)) {
      return {
        valid: false,
        failureMessage,
      }
    }
    const day = Number(value)
    if (day < 1 || day > 31) {
      return {
        valid: false,
        failureMessage,
      }
    }
    if (this.state.isYearValid && this.state.isMonthValid) {
      const maxDay = getMaxDayForYearMonth(
        Number(this.state.year), Number(this.state.month))
      if (day > maxDay) {
        failureMessage.parameters.daysInMonth = maxDay
        return {
          valid: false,
          failureMessage,
        }
      }
    }
    return {
      valid: true
    }
  }

  // The returned value may not be valid
  private getCurrentDate() {
      const state = this.state
      // The month component should always be numeric regardless of user input
      const month = parseMonth(this.state.month, this.props.locale.locale)
      if (state.day) {
        return `${state.year}-${month.padStart(2, "0")}-${(state.day).padStart(2, "0")}`
      } else if (this.props.defaultDay
          && this.props.dayOptional
          && state.isYearValid
          && state.isMonthValid) {
        const day = this.props.defaultDay === "first_day_of_month"
          ? 1
          : getMaxDayForYearMonth(Number(state.year), Number(month))
        return `${state.year}-${month.padStart(2, "0")}-${String(day).padStart(2, "0")}`
      } else {
        return `${state.year}-${month.padStart(2, "0")}`
      }
    }

  private handleYearInput(year: string) {
    this.setState({ year })
  }

  private handleMonthInput(month: string) {
    this.setState({ month })
  }

  private handleDayInput(day: string) {
    this.setState({ day })
  }

  private renderMonth(index: number) {
    return (
      <FlexLayout.Item padding="xs" size="3fr" key="month">
        <TextInput
          name={`${this.props.name}-month`}
          initialValue={this.state.month}
          validator={this.validateMonth}
          onChange={this.handleMonthChange}
          onInput={this.handleMonthInput}
          autoFocus={this.props.autoFocus && index === 0}
          fluid={true}
          label={{
            defaultString: "Month"
          }}
          labelPosition="top"
          testID={this.props.testIDPrefix && `${this.props.testIDPrefix}-month`}
        />
      </FlexLayout.Item>
    )
  }

  private renderDay(index: number) {
    return (
      <FlexLayout.Item padding="xs" size="3fr" key="day">
        <TextInput
          name={`${this.props.name}-day`}
          initialValue={this.state.day}
          validator={this.validateDay}
          onChange={this.handleDayChange}
          onInput={this.handleDayInput}
          autoFocus={this.props.autoFocus && index === 0}
          fluid={true}
          label={{
            defaultString: "Day"
          }}
          labelPosition="top"
          testID={this.props.testIDPrefix && `${this.props.testIDPrefix}-day`}
        />
      </FlexLayout.Item>
    )
  }

  private renderYear(index: number) {
    return (
      <FlexLayout.Item padding="xs" size="4fr" key="year">
        <TextInput
          name={`${this.props.name}-year`}
          initialValue={this.state.year}
          validator={this.validateYear}
          onChange={this.handleYearChange}
          onInput={this.handleYearInput}
          autoFocus={this.props.autoFocus && index === 0}
          fluid={true}
          label={{
            defaultString: "Year"
          }}
          labelPosition="top"
          placeholder={{
            // No need for translation
            defaultString: "YYYY"
          }}
          testID={this.props.testIDPrefix && `${this.props.testIDPrefix}-year`}
        />
      </FlexLayout.Item>
    )
  }

  private renderInputs(locale: string) {
    return getDateComponentOrder(locale).split("").map((opCode, index) => {
      switch (opCode) {
        case "y": return this.renderYear(index)
        case "m": return this.renderMonth(index)
        case "d": return this.renderDay(index)
        default: assert("Unexpected date component code " + opCode)
      }
    })
  }

  public render() {
    return (
      <FlexLayout direction="column" dangerousClassName="flex-layout--height-auto">
        <FlexLayout.Item size="auto">
          <FlexLayout direction="row-ltr">
            {this.renderInputs(this.props.locale.locale)}
          </FlexLayout>
        </FlexLayout.Item>
        <FlexLayout.Item hidden={true}>
          {/*
          A hidden input to facilitate form submission with a single date value.
          Its value must be synchorized with the date component inputs as user types.
          As such, the value is updated for each `input` event.

          I tried using the `change` event. It doesn't work and leads to out-of-sync
          value with the component values during auto form submission. E.g., the three
          components may be 12, 25 and 2025 for month, day and year, respectively,
          but the hidden input's value may be 202-12-25, one letter short for the year.
          This is because when user finished typing 5 for 2025 and pressed Enter to submit
          the form, DateInput handles the change event and updates its state. However,
          the rendering may come later after the form is submitted, causing a partial value
          to be used.
          */}
          <input
            type="hidden"
            name={this.props.name}
            value={this.getCurrentDate()}
            data-testid={this.props.testIDPrefix && renderTestID(`${this.props.testIDPrefix}-date`)}
          />
        </FlexLayout.Item>
      </FlexLayout>
    )
  }
}

export const DateInput = Object.assign(
  withLocale(DateInputBase),
  {
    displayName: "DateInput",
    setDateInputDefaultStringIDs(defaultStringIDs: DateInputStringIDs) {
      DateInputBase.defaultStringIDs = defaultStringIDs
    },
  },
)
